import * as React from 'react'

/* TYPES */

interface TimerConfig {}

interface Props {
  children: React.ReactNode
}

type TimerId = number | null

export interface TimerApi {
  setTimer: (callback: () => void, duration: number) => void
  timerId: TimerId
}

/* HOOK */

export const useTimer = (config: TimerConfig): TimerApi => {
  const timer = React.useRef<TimerId>(null)

  return {
    setTimer: (callback, duration) => {
      timer.current && clearTimeout(timer.current)
      timer.current = window.setTimeout(callback, duration)
    },
    timerId: timer.current,
  }
}

/* DECORATOR */

export const withTimer = <T extends {}>(config: TimerConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { timer: TimerConfig }) => (
  <Component {...props} timer={useTimer({ ...config, ...props.timer })} />
)

/* RENDER PROP */

export const Timer = ({ children }: Props) => {
  const timer = useTimer({})

  return typeof children === 'function' ? children(timer) : children
}

/* CONTEXT */

const noop = (method: string) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<TimerProvider>\``
  )

export const TimerContext = React.createContext<TimerApi>({
  timerId: null,
  setTimer: noop('setTimer'),
})

export const TimerProvider = ({ children }: Props) => (
  <TimerContext.Provider value={useTimer({})}>{children}</TimerContext.Provider>
)

export const consumeTimer = () => React.useContext(TimerContext)
